import { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { SUPPORTED_CHAINS, VALID_CHAIN_IDS, IS_TEST_MODE } from '../contracts/addresses';
import { useTranslation } from 'react-i18next'; // Importar hook de traducción

export function useEthers() {
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [isNetworkCorrect, setIsNetworkCorrect] = useState(false);
  const [connectionInitialized, setConnectionInitialized] = useState(false);
  const { t } = useTranslation(); // Inicializar hook de traducción

  // Verificar si ya hay una wallet conectada al cargar la página
  useEffect(() => {
    const checkExistingConnection = async () => {
      if (window.ethereum && !connectionInitialized) {
        try {
          // Comprobar si ya hay cuentas conectadas
          const accounts = await window.ethereum.request({ method: 'eth_accounts' });
          
          if (accounts && accounts.length > 0) {
            // Ya hay una wallet conectada
            const currentAccount = accounts[0];
            
            // Obtener el proveedor y signer
            const web3Provider = new ethers.providers.Web3Provider(window.ethereum);
            const currentSigner = web3Provider.getSigner();
            
            // Obtener y verificar la red actual
            const network = await web3Provider.getNetwork();
            const currentChainId = network.chainId;
            const isCorrectNetwork = VALID_CHAIN_IDS.includes(currentChainId);
            
            console.log(`Wallet ya conectada. Cuenta: ${currentAccount}, ChainId: ${currentChainId}`);
            
            // Actualizar estados
            setProvider(web3Provider);
            setSigner(currentSigner);
            setAccount(currentAccount);
            setChainId(currentChainId);
            setIsNetworkCorrect(isCorrectNetwork);
            
            // También configurar el proveedor de solo lectura según la red detectada
            setReadonlyProvider(currentChainId);
          } else {
            // No hay wallet conectada, inicializar provider de solo lectura según IS_TEST_MODE
            setReadonlyProvider(IS_TEST_MODE ? 97 : 56);
          }
        } catch (err) {
          console.error("Error al verificar conexión existente:", err);
          // Inicializar provider de solo lectura en caso de error
          setReadonlyProvider(IS_TEST_MODE ? 97 : 56);
        }
        
        setConnectionInitialized(true);
      } else if (!window.ethereum) {
        // No hay MetaMask instalado, inicializar provider de solo lectura
        setReadonlyProvider(IS_TEST_MODE ? 97 : 56);
        setConnectionInitialized(true);
      }
    };
    
    checkExistingConnection();
  }, []); // Se ejecuta solo una vez al montar el componente

  // Función para configurar el provider de solo lectura según chainId
  const setReadonlyProvider = (networkChainId) => {
    try {
      const rpcUrl = networkChainId === 97 
        ? 'https://data-seed-prebsc-1-s1.binance.org:8545/' 
        : 'https://bsc-dataseed.binance.org/';
      
      const readProvider = new ethers.providers.JsonRpcProvider(rpcUrl);
      // Establecer solo si no hay un provider de wallet activo
      if (!provider) {
        console.log(`Inicializando provider de solo lectura para chainId: ${networkChainId}`);
        setProvider(readProvider);
        // Actualizar chainId para asegurar compatibilidad con balances
        setChainId(networkChainId);
      }
    } catch (error) {
      console.error("Error al inicializar proveedor de solo lectura:", error);
    }
  };

  // Función para conectar wallet (similar a la original pero con mejoras)
  const connect = useCallback(async () => {
    try {
      setIsConnecting(true);
      setError(null);

      if (!window.ethereum) {
        throw new Error(t('wallet.error.noMetamask'));
      }

      // Solicitar cuentas
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      
      // Verificar que se obtuvo alguna cuenta
      if (accounts && accounts.length > 0) {
        const currentAccount = accounts[0];
        
        // Obtener proveedor y firmante
        const web3Provider = new ethers.providers.Web3Provider(window.ethereum);
        const currentSigner = web3Provider.getSigner();
        
        // Obtener chainId
        const network = await web3Provider.getNetwork();
        const currentChainId = network.chainId;

        // Verificar si estamos en la red correcta
        const isCorrectNetwork = VALID_CHAIN_IDS.includes(currentChainId);
        
        // Actualizar estados
        setProvider(web3Provider);
        setSigner(currentSigner);
        setAccount(currentAccount);
        setChainId(currentChainId);
        setIsNetworkCorrect(isCorrectNetwork);
        console.log(`Wallet conectada. Cuenta: ${currentAccount}, ChainId: ${currentChainId}`);
      } else {
        throw new Error(t('wallet.error.noAccount'));
      }
    } catch (err) {
      setError(err.message || t('wallet.error.connection'));
      console.error("Error al conectar wallet:", err);
    } finally {
      setIsConnecting(false);
    }
  }, [t]);

  // Función para cambiar de red
  const switchNetwork = useCallback(async (targetChainId) => {
    try {
      if (!window.ethereum) throw new Error(t('wallet.error.noMetamask'));
      
      const chainConfig = Object.values(SUPPORTED_CHAINS)
        .find(chain => chain.chainId === targetChainId);
      
      if (!chainConfig) throw new Error(t('wallet.error.networkNotSupported'));

      try {
        // Primero intentar cambiar a la red existente
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: `0x${targetChainId.toString(16)}` }],
        });
      } catch (switchError) {
        // Si el código de error es 4902, la red no está añadida
        if (switchError.code === 4902) {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: `0x${targetChainId.toString(16)}`,
              chainName: chainConfig.name,
              nativeCurrency: chainConfig.nativeCurrency,
              rpcUrls: [chainConfig.rpcUrl],
              blockExplorerUrls: [chainConfig.explorerUrl],
            }],
          });
        } else {
          throw switchError;
        }
      }
      
      // Reconectar para actualizar estados
      await connect();
      
    } catch (err) {
      setError(err.message || t('wallet.error.networkSwitch'));
      console.error("Error al cambiar de red:", err);
    }
  }, [connect, t]);

  // Escuchar eventos de cambio de cuenta y red
  useEffect(() => {
    if (window.ethereum) {
      const handleAccountsChanged = async (accounts) => {
        console.log("Cambio de cuentas detectado:", accounts);
        if (accounts.length > 0) {
          setAccount(accounts[0]);
          
          // Actualizar signer con la nueva cuenta
          if (provider) {
            const web3Provider = new ethers.providers.Web3Provider(window.ethereum);
            setSigner(web3Provider.getSigner());
          }
        } else {
          // Desconexión
          setAccount(null);
          setSigner(null);
        }
      };

      const handleChainChanged = async (chainIdHex) => {
        const newChainId = parseInt(chainIdHex, 16);
        console.log(`Cambio de red detectado. Nuevo chainId: ${newChainId}`);
        
        setChainId(newChainId);
        setIsNetworkCorrect(VALID_CHAIN_IDS.includes(newChainId));
        
        // Actualizar provider y signer con la nueva red
        const web3Provider = new ethers.providers.Web3Provider(window.ethereum);
        setProvider(web3Provider);
        
        if (account) {
          setSigner(web3Provider.getSigner());
        }
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);

      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      };
    }
  }, [provider, account]);

  // Crear contrato con ethers
  const getContract = useCallback((address, abi) => {
    if (!address || !abi) return null;

    try {
      // Usar signer si está disponible, de lo contrario usar provider
      const contractProvider = signer || provider;
      if (!contractProvider) return null;

      return new ethers.Contract(address, abi, contractProvider);
    } catch (err) {
      console.error("Error al crear instancia del contrato:", err);
      return null;
    }
  }, [provider, signer]);

  return {
    provider,
    signer,
    account,
    chainId,
    isConnected: !!account,
    isConnecting,
    connect,
    switchNetwork,
    error,
    isNetworkCorrect,
    getContract
  };
}
